import { ArrowDownIcon, ArrowUpIcon, PlusIcon } from '@heroicons/react/24/outline'
import { getBorrowerInitials } from 'components/GlobalLoanSearch/utils'
import { LayoutLoading } from 'components/LayoutLoading'
import { TextHighlighter } from 'components/TextHighlighter'
import type { IAccountingVendor } from 'pages/Admin/Accounting/AccountingVendors/types'

export const PayeeSearchResults = ({
  query,
  vendors,
  total,
  loading,
  activeIndex,
  categories,
  onSelect,
  onActiveIndexChange,
  onAddNew,
}: {
  query: string
  vendors: IAccountingVendor[]
  total: number
  loading: boolean
  activeIndex: number
  categories: Record<number, string>
  onSelect: (vendor: IAccountingVendor) => void
  onActiveIndexChange: (index: number) => void
  onAddNew?: (name: string) => void
}) => {
  const hasQuery = query.trim().length > 0
  const trimmedQuery = query.trim()
  const exactMatch = vendors.some((vendor) => (vendor.name || '').toLowerCase() === trimmedQuery.toLowerCase())

  return (
    <div className="PayeeSearch-results relative z-[70] overflow-hidden rounded-xl border border-gray-200/80 bg-white shadow-2xl">
      <LayoutLoading show={loading} />
      {!loading && !vendors.length && hasQuery && !onAddNew && (
        <div className="px-4 py-10 text-center text-sm text-gray-500">No payees found.</div>
      )}
      {!!vendors.length && (
        <ul className="max-h-72 overflow-y-auto p-1.5">
          {vendors.map((vendor, index) => {
            const isActive = index === activeIndex
            const typeLabel = categories[vendor.type] || ''
            return (
              <li key={vendor.id || index}>
                <button
                  type="button"
                  className={`group w-full rounded-lg px-2.5 py-2.5 text-left transition-colors focus:outline-none ${
                    isActive ? 'bg-sky-50' : 'hover:bg-gray-50'
                  }`}
                  onMouseDown={(event) => {
                    event.preventDefault()
                    onSelect(vendor)
                  }}
                  onMouseEnter={() => onActiveIndexChange(index)}
                >
                  <div className="flex items-center gap-3">
                    <div className="flex h-9 w-9 shrink-0 items-center justify-center rounded-full bg-sky-100 text-xs font-semibold text-sky-700">
                      {getBorrowerInitials(vendor.name || '?')}
                    </div>
                    <div className="min-w-0 flex-1">
                      <div className="truncate text-sm font-semibold text-gray-900">
                        <TextHighlighter searchText={query} text={vendor.name || ''} />
                      </div>
                      <div className="mt-0.5 flex min-w-0 items-center gap-1.5 text-xs text-gray-500">
                        {vendor.ein && (
                          <span className="shrink-0 font-medium tabular-nums text-gray-400">
                            <TextHighlighter searchText={query} text={vendor.ein} />
                          </span>
                        )}
                        {vendor.ein && vendor.address && <span className="shrink-0 text-gray-300">·</span>}
                        {vendor.address && (
                          <span className="truncate">
                            <TextHighlighter searchText={query} text={vendor.address} />
                          </span>
                        )}
                      </div>
                    </div>
                    {typeLabel && (
                      <div className="shrink-0">
                        <div className="inline-flex rounded-full bg-gray-100 px-2 py-0.5 text-[11px] font-medium text-gray-600">
                          {typeLabel}
                        </div>
                      </div>
                    )}
                  </div>
                </button>
              </li>
            )
          })}
        </ul>
      )}
      {hasQuery && !loading && onAddNew && !exactMatch && (
        <button
          type="button"
          className="flex w-full items-center gap-2 border-t border-gray-100 px-4 py-2.5 text-left text-sm font-medium text-shade-blue hover:bg-gray-50"
          onMouseDown={(event) => {
            event.preventDefault()
            onAddNew(trimmedQuery)
          }}
        >
          <PlusIcon className="h-4 w-4" />
          <span>
            Add <span className="font-semibold">&quot;{trimmedQuery}&quot;</span> as new vendor
          </span>
        </button>
      )}
      {hasQuery && (
        <div className="flex items-center justify-between gap-3 border-t border-gray-100 px-3 py-2.5 text-[11px] text-gray-400">
          <span className="flex items-center gap-1">
            <ArrowUpIcon className="h-3 w-3" />
            <ArrowDownIcon className="h-3 w-3" />
            <span>to navigate</span>
          </span>
          <span className="flex items-center gap-1">
            <kbd className="rounded border border-gray-200 bg-gray-50 px-1 py-0.5 text-[10px] font-medium text-gray-500">
              ↵
            </kbd>
            <span>to select</span>
          </span>
          <span className="text-gray-300">
            {vendors.length} of {total}
          </span>
        </div>
      )}
    </div>
  )
}
